import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, OutlinedInput, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { cast, handleApiError, notify } from '~/utils/helper';
import { IAccount, IAccountUpdate } from '~/utils/interface/account';
import accountApi from '~/apis/account';
import { ROLE_TECHINICIAN } from '~/utils/constants/account';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetAuthUserInfo } from '~/thunks/auth/authThunk';

type EditTechnicianDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IAccountUpdate;
  mode?: string;
  parent_username?: string;
};

const initialValues: IAccountUpdate = {
  username: '',
};

const EditTechnicianDialog = (props: EditTechnicianDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/technician');

  const { authUser } = useAppSelector((state) => state.auth);

  const [technician, setTechnician] = useState<IAccount>();
  const [refreshData, setRefreshData] = useState<boolean>(false);

  useEffect(() => {
    if (props.open && props.data) {
      getTechnician();
      dispatch(handleGetAuthUserInfo());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values) => {
      if (props.mode === DIALOG_MODE_CREATE) {
        try {
          await accountApi.createByRole({ role: ROLE_TECHINICIAN }, {
            ...values,
            // If agent then parent_username = distributor
            // If technician then parent_username = agent
            parent_username: getParentUserName(),
          } as any);
          notify('success', t('editTechnicianDialog.addSuccess'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      } else if (props.mode === DIALOG_MODE_EDIT && technician?.username) {
        try {
          let dataUpdate: IAccountUpdate = values;
          dataUpdate.username = technician?.username;
          await accountApi.updateByRole(dataUpdate as any);
          notify('success', t('editTechnicianDialog.editSuccess'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }
    },
  });

  const getTechnician = async () => {
    try {
      const response = await accountApi.getByRoleList(ROLE_TECHINICIAN, { username: props.data?.username });
      if (response.data?.data?.length) {
        setTechnician(response.data.data[0]);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  useEffect(() => {
    if (props.mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(technician?.name));
      form.setFieldValue('address', cast(technician?.address));
      form.setFieldValue('phone', cast(technician?.phone));
      form.setFieldValue('email', cast(technician?.email));
      form.setFieldValue('created_at', cast(technician?.created_at));
    }

    if (props.mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('password', '');
      form.setFieldValue('username', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technician, props.mode]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    setTechnician(undefined);
    setRefreshData(false);
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const getParentUserName = () => {
    if (props?.parent_username) return props?.parent_username;
    else if (technician?.parent_username) return technician?.parent_username;
    return authUser.username;
  };

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false, refreshData)} fullWidth maxWidth='sm'>
        <DialogTitle className='bg-[#005F73]'>
          {props.mode === DIALOG_MODE_CREATE ? t('createTechnicianDialog.title') : t('editTechnicianDialog.title')}
          <IconButton onClick={() => handleSetOpen(false, refreshData)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BaseRow>
            <Box className='w-full relative'>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between items-center'>
                <Box className='font-bold'>{t('editTechnicianDialog.name')}:</Box>
                <TextField
                  size='medium'
                  className='w-2/3'
                  name='name'
                  placeholder={t('editTechnicianDialog.name')}
                  value={form.values?.name}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.name)}
                  helperText={form.errors?.name}
                />
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between items-center'>
                <Box className='font-bold'>{t('editTechnicianDialog.address')}:</Box>
                <TextField
                  size='medium'
                  className='w-2/3'
                  name='address'
                  placeholder={t('editTechnicianDialog.address')}
                  value={form.values?.address}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.address)}
                  helperText={form.errors?.address}
                />
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between items-center'>
                <Box className='font-bold'>{t('editTechnicianDialog.phone')}:</Box>
                <TextField
                  size='medium'
                  className='w-2/3'
                  name='phone'
                  placeholder={t('editTechnicianDialog.phone')}
                  value={form.values?.phone}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.phone)}
                  helperText={form.errors?.phone}
                />
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between items-center'>
                <Box className='font-bold'>{t('editTechnicianDialog.email')}:</Box>
                <TextField
                  size='medium'
                  className='w-2/3'
                  name='email'
                  placeholder={t('editTechnicianDialog.email')}
                  value={form.values?.email}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.email)}
                  helperText={form.errors?.email}
                />
              </Box>
              <Box className='flex flex-row gap-3.5 my-4 justify-between items-center'>
                <Box className='font-bold'>{t('editTechnicianDialog.parentUsername')}:</Box>
                <Box className='font-semibold'>{getParentUserName()}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between items-center'>
                <Box className='font-bold'>{t('editTechnicianDialog.createdAt')}:</Box>
                <Box className='w-2/3'>
                  <BaseDatePicker
                    portalId='datePicker-portalId'
                    value={form.values.created_at}
                    dateFormat='YYYY-MM-DD'
                    onChange={(date) => form.setFieldValue('created_at', dayjs(date).format('YYYY-MM-DD HH:mm:ss'))}
                    placeholder={t('select')}
                    className='w-full'
                  />
                </Box>
              </Box>

              {props.mode === DIALOG_MODE_CREATE ? (
                <Box>
                  <Box className='font-bold text-[#005F73] text-[13px] mt-[40px] mb-4'>
                    {t('editTechnicianDialog.infoLogin')}
                  </Box>
                  <Box className='flex flex-row gap-3.5 mb-4 justify-between items-center'>
                    <Box className='font-bold'>{t('editTechnicianDialog.username')}:</Box>
                    <TextField
                      size='medium'
                      className='w-2/3'
                      name='username'
                      placeholder={t('editTechnicianDialog.username')}
                      value={form.values?.username}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.username)}
                      helperText={form.errors?.username}
                    />
                  </Box>
                  <Box className='flex flex-row gap-3.5 mb-4 justify-between items-center'>
                    <Box className='font-bold'>{t('editTechnicianDialog.password')}:</Box>
                    <OutlinedInput
                      id='password'
                      className='w-2/3'
                      type='password'
                      placeholder={t('editTechnicianDialog.password')}
                      name='password'
                      value={form.values.password}
                      onChange={form.handleChange}
                    />
                  </Box>
                </Box>
              ) : null}

              <Button
                variant='contained'
                color='secondary'
                className='min-w-[125px] flex ml-auto'
                onClick={() => form.handleSubmit()}
              >
                {props.mode === DIALOG_MODE_EDIT ? t('editTechnicianDialog.save') : t('createTechnicianDialog.add')}
              </Button>
            </Box>
          </BaseRow>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(EditTechnicianDialog);
